import { generateCustomerPortalLinkCloudFunction } from '@/firebase/init';
import { useOrganizationStore } from '@/stores/organizationStore';
import { Card, Flex, Text } from '@tremor/react';
import { Timestamp } from 'firebase/firestore';
import { useUserPermissionsStore } from '@/stores/userPermissionsStore';
import { NavLink, useSearchParams } from 'react-router-dom';
import { SubscriptionButton } from '@/components/layouts/Buttons/SubscriptionButton/SubscriptionButton';
import { Benefits } from './Benefits';
import { Details } from './Details';
import { useState } from 'react';
import { showToast } from '@/utils/toasts';

export interface SubscriptionDetailsProps {
  isSubscription: boolean;
  subscriptionTier: string;
  userName: string;
  userEmail: string;
  createdAt: Timestamp;
  subscriptionUserId: string;
}

export function SubscriptionDetailsCard({
  isSubscription,
  createdAt,
  subscriptionTier,
  userEmail,
  userName,
  subscriptionUserId,
}: SubscriptionDetailsProps) {
  const selectedOrganization = useOrganizationStore(
    (state) => state.selectedOrganization,
  );
  const userId = useUserPermissionsStore((state) => state.userId);
  const organizationId = selectedOrganization?.id as string;
  const [searchParams] = useSearchParams();
  const subscriptionsUrl = new URL('/subscriptions', window.location.origin);
  subscriptionsUrl.searchParams.set(
    'workspaceId',
    searchParams.get('workspaceId') as string,
  );
  const [isLoading, setIsLoading] = useState(false);
  return (
    <Card className="shadow-none bg-white rounded-xl border-gray-200 border-1 mt-8">
      <Flex alignItems="stretch">
        <div className="flex-[7]">
          {isSubscription ? (
            <Details
              createdAt={createdAt}
              subscriptionTier={subscriptionTier}
              userEmail={userEmail}
              userName={userName}
              subscriptionUserId={subscriptionUserId}
            />
          ) : (
            <Benefits />
          )}
        </div>
        <Flex
          justifyContent="center"
          flexDirection="col"
          className="flex-[3] border-l-2 border-[rgb(225 227 242)]"
        >
          {isSubscription ? (
            <SubscriptionButton
              isLoading={isLoading}
              disabled={userId !== subscriptionUserId}
              onClick={async () => {
                setIsLoading(true);
                try {
                  const link = await generateCustomerPortalLinkCloudFunction({
                    organizationId,
                  });
                  window.open(link.data as string, '_blank', 'noopener,noreferrer');
                } catch (e) {
                  showToast(
                    'Error!',
                    'Could not reach customer portal, try later',
                    'error',
                  );
                } finally {
                  setIsLoading(false);
                }
              }}
            >
              Manage Subscription
            </SubscriptionButton>
          ) : (
            <>
              <Text className="mt-4 text-[rgb(55 65 81)]">
                Unlock premium features with a subscription
              </Text>
              <NavLink
                className="w-[300px] h-fit 
                bg-gradient-to-br from-primary to-secondary 
                text-white font-bold text-center 
                rounded px-4 py-[0.6125rem] 
                shadow-[0_4px_6px_rgba(222,42,72,0.25)] 
                transition-transform duration-300 ease-in-out 
                hover:scale-105 
                disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100
                focus:outline-none focus:ring-4 focus:ring-[rgba(186,38,52,0.5)]"
                to={subscriptionsUrl.toString()}
              >
                Subscribe Now
              </NavLink>
            </>
          )}
        </Flex>
      </Flex>
    </Card>
  );
}
